import { DateTime } from "luxon";
import { z } from "zod";

export const REASON_CODES = [
  "CONTRACT_CHANGE",
  "APPROVED_EXCEPTION",
  "PRICING_UPDATE",
  "TEMPORARY_CHANGE",
  "CORRECTION",
  "PRODUCT_CHANGE",
  "OTHER",
] as const;

const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;

export type ReasonCodeValue = (typeof REASON_CODES)[number];

export const reasonCodeSchema = z.enum(REASON_CODES, {
  errorMap: (issue, ctx) => {
    if (
      issue.code === "invalid_type" &&
      (ctx.data === undefined || ctx.data === null)
    ) {
      return { message: "Required" };
    }
    return { message: `Invalid reason code: ${String(ctx.data)}` };
  },
});

// Accepts a JS Date or a valid Luxon DateTime; outputs a JS Date normalized
// to midnight UTC (any time component is stripped).
const flexDateSchema = z
  .union(
    [
      z.date({ errorMap: () => ({ message: "Required" }) }),
      z
        .custom<DateTime>(
          (val): val is DateTime => val instanceof DateTime && val.isValid
        )
        .transform(dt => dt.toJSDate()),
    ],
    {
      errorMap: () => ({ message: "Required" }),
    }
  )
  .transform(date => {
    const normalized = new Date(date.getTime());
    normalized.setUTCHours(0, 0, 0, 0);
    return normalized;
  });

const nullableFlexDateSchema = flexDateSchema.nullable();

// Preprocess a reason-code field: "" (MUI Select default) becomes null so that
// a union-abort on the empty string does not prevent superRefine from running.
const nullableReasonCodeField = z.preprocess(
  (val): unknown => (val === "" ? null : val),
  reasonCodeSchema.nullable()
);

const basePricingAssignmentFields = {
  primaryBundleId: z.string().min(1, "Required"),
  // Use nullable so that an empty string (preprocessed to null) passes base
  // validation and does not abort superRefine.
  primaryReasonCode: nullableReasonCodeField,
  primaryNotes: z.string().nullish(),
  // Accept null so the form's default null value does not abort superRefine.
  effectiveDate: nullableFlexDateSchema,
  expirationDate: nullableFlexDateSchema.optional(),
  afterBundleId: z.string().nullish(),
  afterReasonCode: z.preprocess(
    (val): unknown => (val === "" ? null : val),
    reasonCodeSchema.nullable().optional()
  ),
  afterNotes: z.string().nullish(),
};

type PricingAssignmentData = {
  primaryBundleId: string;
  primaryReasonCode: ReasonCodeValue | null;
  primaryNotes?: string | null;
  effectiveDate: Date | null;
  expirationDate?: Date | null;
  afterBundleId?: string | null;
  afterReasonCode?: ReasonCodeValue | null;
  afterNotes?: string | null;
};

function refinePricingAssignment(
  data: PricingAssignmentData,
  ctx: z.RefinementCtx,
  { requireAfterBundle }: { requireAfterBundle: boolean }
): void {
  if (!data.primaryReasonCode) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Required",
      path: ["primaryReasonCode"],
    });
  }
  if (!data.effectiveDate) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Required",
      path: ["effectiveDate"],
    });
  }
  if (data.primaryReasonCode === "OTHER" && !data.primaryNotes?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Notes are required when the change reason is Other",
      path: ["primaryNotes"],
    });
  }

  if (data.expirationDate) {
    if (data.effectiveDate) {
      const oneDayAfter = new Date(
        data.effectiveDate.getTime() + ONE_DAY_IN_MS
      );
      if (data.expirationDate < oneDayAfter) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Expiration date must be at least one day after the effective date",
          path: ["expirationDate"],
        });
      }
    }
    // afterReasonCode/afterNotes without an afterBundleId can occur when the
    // form held stale values that became hidden (e.g. afterBundleId switched
    // to "None" after a reason was already chosen). Treat the bundle as
    // required whenever any after-field carries data.
    const hasAfterReasonOrNotes =
      !!data.afterReasonCode || !!data.afterNotes?.trim();
    if ((requireAfterBundle || hasAfterReasonOrNotes) && !data.afterBundleId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Required",
        path: ["afterBundleId"],
      });
    } else if (
      data.afterBundleId &&
      data.afterBundleId === data.primaryBundleId
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "After-expiration bundle must be different from the primary bundle",
        path: ["afterBundleId"],
      });
    }
    if (data.afterBundleId && !data.afterReasonCode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Required",
        path: ["afterReasonCode"],
      });
    } else if (requireAfterBundle && !data.afterReasonCode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Required",
        path: ["afterReasonCode"],
      });
    }
    if (data.afterReasonCode === "OTHER" && !data.afterNotes?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Notes are required when the change reason is Other",
        path: ["afterNotes"],
      });
    }
  }
}

export const changeTenantPricingAssignmentSchema = z
  .object(basePricingAssignmentFields)
  .superRefine((data, ctx) => {
    refinePricingAssignment(data, ctx, { requireAfterBundle: true });
  });

// Subaccount assignments allow gaps, so the after-expiration bundle is
// optional; the rest of the validation mirrors the tenant schema.
export const createSubaccountPricingAssignmentsSchema = z
  .object({
    ...basePricingAssignmentFields,
    subaccountIds: z.array(z.string().min(1)).min(1, "Required"),
  })
  .superRefine((data, ctx) => {
    refinePricingAssignment(data, ctx, { requireAfterBundle: false });
  });
